/**
 * DemoExam.tsx
 * Scientific exam interface with modern UX patterns and focus optimization.
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@vsat/shared/components';
import ScientificExamLayout from './take/ScientificExamLayout';
import GenericQuestionContent from './take/GenericQuestionContent';
import TabReturnModal from './take/TabReturnModal';
import SecurityResetModal from './take/SecurityResetModal';
import ExamTourIntro from './ExamTourIntro';
import ExamTour from './ExamTour';
import { useExamSecurity } from '@vsat/shared/hooks/useExamSecurity';
import { useTourStore } from '@vsat/shared/hooks/useTourStore';
import type {
  DemoAnswer,
  DemoAnswerMap,
  DemoQuestion,
  DemoResultSummary,
  DemoScoreItem,
  ExamConfig,
} from './types';
import { useCountdown, createExpiryTime } from '@vsat/shared/hooks/useCountdown';
import { expandClusterQuestions } from '@vsat/shared/lib/clusterUtils';
import { apiService } from '@vsat/shared/lib/api';

/** Props for DemoExam. */
interface Props {
  questions: DemoQuestion[];
  durationMinutes: number;
  onSubmit: (summary: DemoResultSummary, answers: DemoAnswerMap) => void;
  onExit: () => void;
  examConfig?: ExamConfig;
  examInfo?: ExamInfoMeta;
  onPageChange?: (index: number) => void;
}

interface ExamInfoMeta {
  id?: string;
  title: string;
  subject?: string;
  totalQuestions?: number;
  totalPoints?: number;
  price?: number;
  passingScore?: number | null;
  standard?: string | null; // Exam standard: 'VSAT', 'THPT', or null
}

/** Helper: score V-SAT 2025 True/False question with graded scoring */
function scoreTrueFalseAnswer(
  value: Record<string, boolean> | null,
  items: Array<{ id: string; correct: boolean }> | undefined
): number {
  if (!value || !items) return 0;

  const correctCount = items.filter(item => value[item.id] === item.correct).length;

  // V-SAT 2025 graded scoring according to official documentation:
  // 1/4 correct = 1 point, 2/4 = 2 points, 3/4 = 3 points, 4/4 = 6 points
  switch (correctCount) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 4:
      return 6;
    default:
      return 0;
  }
}

/** Helper: score multiple choice question - V-SAT 2025 standard */
function scoreMultipleChoiceAnswer(
  value: number | null,
  correctAnswer: number | undefined
): number {
  if (value === null || value === undefined || correctAnswer === undefined) return 0;

  // V-SAT 2025: Each correct MC question = 6 points (according to official documentation)
  return value === correctAnswer ? 6 : 0;
}

/** Helper: score matching answer with partial credit (1.5 points per correct pair) */
function scoreMatchingAnswerPartial(
  value: Record<string, string | null> | null,
  correct: Record<string, string> | undefined
): number {
  if (!value || !correct) return 0;

  const correctKeys = Object.keys(correct);
  const correctPairs = correctKeys.filter(promptId => value[promptId] === correct[promptId]).length;

  // V-SAT 2025: 1.5 points per correct pair, 6 points if all 4 pairs correct
  if (correctPairs === 4) return 6; // Bonus for perfect score
  return correctPairs * 1.5; // 1.5 points per correct pair
}

/** Helper: score a short answer by keyword inclusion (case-insensitive) - V-SAT 2025 */
function scoreShortAnswer(value: string | null, keywords: string[] | undefined): number {
  if (!value || !keywords || keywords.length === 0) return 0;

  const normalizedValue = value.trim().toLowerCase();
  const hasCorrectAnswer = keywords.some(keyword =>
    normalizedValue.includes(keyword.toLowerCase())
  );

  // V-SAT 2025: Each correct short answer = 6 points (according to official documentation)
  return hasCorrectAnswer ? 6 : 0;
}

/** Helper: score fill blank question - V-SAT 2025 standard */
function scoreFillBlank(
  value: string[] | string | Record<string, string> | null,
  fillBlankAnswers: any[] | string[] | undefined,
  question?: any // Optional question object to extract correct answers from content.blanks
): number {
  if (!value || !fillBlankAnswers || fillBlankAnswers.length === 0) return 0;

  // Extract correct answers from fillBlankAnswers
  // fillBlankAnswers can be:
  // 1. Array of strings (legacy format)
  // 2. Array of objects with { blankId, correctAnswer, accepted, acceptableAnswers }
  // 3. We can also extract from question.content.blanks if available
  let correctAnswers: string[] = [];

  // Try to extract from question.content.blanks first (most reliable)
  if (question?.content?.blanks && Array.isArray(question.content.blanks)) {
    correctAnswers = question.content.blanks
      .map((blank: any) => {
        // Priority: accepted > acceptableAnswers > correctAnswer
        if (blank.accepted && Array.isArray(blank.accepted) && blank.accepted.length > 0) {
          return String(blank.accepted[0]);
        }
        if (
          blank.acceptableAnswers &&
          Array.isArray(blank.acceptableAnswers) &&
          blank.acceptableAnswers.length > 0
        ) {
          return String(blank.acceptableAnswers[0]);
        }
        if (blank.correctAnswer) {
          return String(blank.correctAnswer);
        }
        return '';
      })
      .filter((ans: string) => ans !== '');
  }

  // If no correct answers from content.blanks, extract from fillBlankAnswers
  if (correctAnswers.length === 0) {
    correctAnswers = fillBlankAnswers
      .map((item: any) => {
        if (typeof item === 'string') {
          return item;
        }
        if (typeof item === 'object' && item !== null) {
          // Priority: accepted > acceptableAnswers > correctAnswer
          if (item.accepted && Array.isArray(item.accepted) && item.accepted.length > 0) {
            return String(item.accepted[0]);
          }
          if (
            item.acceptableAnswers &&
            Array.isArray(item.acceptableAnswers) &&
            item.acceptableAnswers.length > 0
          ) {
            return String(item.acceptableAnswers[0]);
          }
          if (item.correctAnswer) {
            return String(item.correctAnswer);
          }
          if (item.answer) {
            return String(item.answer);
          }
        }
        return '';
      })
      .filter((ans: string) => ans !== '');
  }

  if (correctAnswers.length === 0) return 0;

  // Handle different answer formats
  let answers: string[];

  if (typeof value === 'string') {
    // Legacy single-blank format: value is a string
    answers = [value];
  } else if (Array.isArray(value)) {
    // Array format: value is string[]
    answers = value;
  } else if (typeof value === 'object' && value !== null) {
    // Enhanced multi-blank format: value is Record<string, string>
    // Convert object to array of answers (values only, in blank order)
    answers = Object.values(value);
  } else {
    answers = [];
  }

  let correctCount = 0;

  answers.forEach((answer, index) => {
    if (!correctAnswers[index]) return;

    const correctAnswer = correctAnswers[index];
    // Ensure both are strings before comparing
    const answerStr = typeof answer === 'string' ? answer : String(answer || '');
    const correctStr =
      typeof correctAnswer === 'string' ? correctAnswer : String(correctAnswer || '');

    if (answerStr.trim().toLowerCase() === correctStr.trim().toLowerCase()) {
      correctCount++;
    }
  });

  // V-SAT 2025: Each fill blank question = 6 points total (according to official documentation)
  const totalBlanks = correctAnswers.length;
  return totalBlanks > 0 ? (correctCount / totalBlanks) * 6 : 0;
}

/** Helper: score multiple choice question - V-SAT 2025 standard */
function scoreMultipleChoice(value: number | null, correctAnswer: number | undefined): number {
  if (value === null || value === undefined || correctAnswer === undefined) return 0;

  // V-SAT 2025: Each correct MC question = 6 points
  return value === correctAnswer ? 6 : 0;
}

function resolveSelectedOptionIndex(
  question: DemoQuestion,
  value: DemoAnswer['value']
): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && value.trim() !== '' && numeric.toString() === value) {
      return numeric;
    }

    if (question.options && question.options.length > 0) {
      const index = question.options.findIndex(option => {
        if (typeof option === 'string') {
          return option === value;
        }
        return option?.id === value;
      });
      return index >= 0 ? index : null;
    }
  }

  return null;
}

/** Compute final scores per question and grand totals with V-SAT 2025 scoring. */
function computeScores(
  questions: DemoQuestion[],
  ans: DemoAnswerMap
): {
  items: DemoScoreItem[];
  total: number;
  earned: number;
  breakdown: {
    trueFalse: { total: number; earned: number };
    multipleChoice: { total: number; earned: number };
    matching: { total: number; earned: number };
    shortAnswer: { total: number; earned: number };
    essay: { total: number; earned: number };
    fillBlank: { total: number; earned: number };
  };
} {
  const items: DemoScoreItem[] = [];
  let total = 0;
  let earned = 0;

  const breakdown = {
    trueFalse: { total: 0, earned: 0 },
    multipleChoice: { total: 0, earned: 0 },
    matching: { total: 0, earned: 0 },
    shortAnswer: { total: 0, earned: 0 },
    essay: { total: 0, earned: 0 },
    fillBlank: { total: 0, earned: 0 },
  };

  for (const q of questions) {
    if (!q) continue;
    total += q.points;
    const a = ans[q.id]?.value ?? null;
    let correct = false;
    let earnedPoints = 0;
    let partialCredit = 0;

    if (q.type === 'mc') {
      const answerIndex = resolveSelectedOptionIndex(q, a);
      earnedPoints = scoreMultipleChoice(answerIndex, q.correct as number);
      correct = earnedPoints > 0;
      breakdown.multipleChoice.total += q.points;
      breakdown.multipleChoice.earned += earnedPoints;
    }

    if (q.type === 'tf') {
      const answerIndex = resolveSelectedOptionIndex(q, a);
      earnedPoints = scoreMultipleChoice(answerIndex, (q.correct as number) ? 1 : 0);
      correct = earnedPoints > 0;
      breakdown.multipleChoice.total += q.points;
      breakdown.multipleChoice.earned += earnedPoints;
    }

    if (q.type === 'trueFalse') {
      earnedPoints = scoreTrueFalseAnswer(a as Record<string, boolean>, q.trueFalseItems);
      correct = earnedPoints === q.points;
      partialCredit = earnedPoints / q.points;
      breakdown.trueFalse.total += q.points;
      breakdown.trueFalse.earned += earnedPoints;
    }

    if (q.type === 'short') {
      earnedPoints = scoreShortAnswer((a as string) ?? null, q.correctKeywords);
      correct = earnedPoints > 0;
      breakdown.shortAnswer.total += q.points;
      breakdown.shortAnswer.earned += earnedPoints;
    }

    if (q.type === 'essay') {
      // Essay scoring would be manual - for demo, give partial credit based on word count
      const wordCount = ((a as string) || '').trim().split(/\s+/).length;
      const minWords = q.minWords || (q as any).content?.minWords || 0;
      if (wordCount >= minWords) {
        earnedPoints = Math.min(q.points, Math.floor(q.points * 0.8)); // 80% for meeting minimum
        correct = wordCount >= minWords;
      }
      breakdown.essay.total += q.points;
      breakdown.essay.earned += earnedPoints;
    }

    if (q.type === 'matching') {
      earnedPoints = scoreMatchingAnswerPartial(
        a as Record<string, string | null>,
        q.matchingCorrect
      );
      correct = earnedPoints === q.points;
      partialCredit = earnedPoints / q.points;
      breakdown.matching.total += q.points;
      breakdown.matching.earned += earnedPoints;
    }

    if (q.type === 'fillBlank' || q.type === 'short_text') {
      // Type guard: fillBlank answers can be string, string[], or Record<string, string>
      const fillBlankValue = a as string | string[] | Record<string, string> | null;
      earnedPoints = scoreFillBlank(fillBlankValue, q.fillBlankAnswers, q);
      correct = earnedPoints === q.points;
      partialCredit = earnedPoints / q.points;
      breakdown.fillBlank.total += q.points;
      breakdown.fillBlank.earned += earnedPoints;
    }

    earned += earnedPoints;
    items.push({
      questionId: q.id,
      points: q.points,
      earned: earnedPoints,
      correct,
      partialCredit,
      breakdown:
        q.type === 'trueFalse'
          ? {
              correctItems: q.trueFalseItems
                ? Object.keys((a as Record<string, boolean>) || {}).filter(
                    id =>
                      (a as Record<string, boolean>)[id] ===
                      q.trueFalseItems?.find(item => item.id === id)?.correct
                  ).length
                : 0,
              totalItems: q.trueFalseItems?.length || 0,
            }
          : undefined,
    });
  }

  return { items, total, earned, breakdown };
}

/** Scientific exam component with focus optimization. */
export default function DemoExam({
  questions: rawQuestions,
  durationMinutes,
  onSubmit,
  onExit,
  examConfig,
  examInfo,
  onPageChange,
}: Props) {
  const { t } = useTranslation();
  // Expand cluster questions into individual renderable questions
  const questions = useMemo(() => expandClusterQuestions(rawQuestions), [rawQuestions]);

  // Store shuffled order for matching questions (keyed by question ID)
  // This ensures the order remains consistent throughout the exam session
  const [shuffledOrders, setShuffledOrders] = useState<Record<string, string[]>>({});

  // Use provided exam config or default
  const defaultExamConfig = {
    id: 'demo',
    title: 'Demo Exam',
    type: 'free' as const,
    securityConfig: {
      tabRestriction: true,
      violationLimit: 3,
      restrictionLevel: 'strict' as const,
    },
  };

  const finalExamConfig = examConfig || defaultExamConfig;
  const finalExamInfo: ExamInfoMeta = examInfo || {
    id: finalExamConfig.id,
    title: finalExamConfig.title,
    subject: questions[0]?.subject,
    totalQuestions: questions.length,
    totalPoints: questions.reduce((sum, q) => sum + (q.points || 0), 0),
    price: (finalExamConfig as any).price || 0,
    passingScore:
      typeof (finalExamConfig as any).passingScore === 'number'
        ? (finalExamConfig as any).passingScore
        : null,
    standard: (finalExamConfig as any).standard || null,
  };

  // Tour state
  const { hasSeenTour, isRunning: isTourRunning, startTour, endTour } = useTourStore();
  const [showTourIntro, setShowTourIntro] = useState(false);

  // Storage key for persisting exam state
  const storageKey = useMemo(() => {
    const examId = finalExamInfo?.id || 'demo';
    return `exam_session_${examId}`;
  }, [finalExamInfo?.id]);

  // Session state from backend
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  // Restore exam state from backend session or storage
  const restoreExamState = useCallback(async (): Promise<{
    answers: DemoAnswerMap;
    index: number;
    startedAt: number;
    expiryTime: number;
    sessionId?: string | null;
    flags?: Record<string, boolean>;
    expired?: boolean; // Flag to indicate if session is expired
  } | null> => {
    // If we have an exam ID, try to restore from backend first
    if (finalExamInfo?.id) {
      try {
        const sessionStatus = await apiService.getSessionStatus(finalExamInfo.id);

        // Check if session exists (even if expired)
        // CRITICAL: Only proceed if backend has a valid sessionId
        // Backend returns { sessionId: null, status: 'NOT_FOUND' } when no session exists
        if (sessionStatus && sessionStatus.sessionId && sessionStatus.status !== 'NOT_FOUND') {
          // Handle expired session
          if (sessionStatus.expired || sessionStatus.remainingTime <= 0) {
            // Return expired state - frontend will handle auto-submit
            return {
              answers: sessionStatus.answers || {},
              index: 0,
              startedAt: sessionStatus.startedAt
                ? new Date(sessionStatus.startedAt).getTime()
                : Date.now(),
              expiryTime: Date.now(), // Set to now to trigger immediate timeout
              sessionId: sessionStatus.sessionId,
              flags: {},
              expired: true, // Mark as expired
            };
          }

          // Restore from backend session (still has time)
          if (sessionStatus.status === 'IN_PROGRESS' && sessionStatus.remainingTime > 0) {
            // Restore from backend session
            const now = Date.now();
            const expiresAt = now + sessionStatus.remainingTime; // Calculate expiry from remaining time

            setSessionId(sessionStatus.sessionId);

            // Restore answers from backend (if available) or sessionStorage
            let restoredAnswers: DemoAnswerMap = {};
            let restoredFlags: Record<string, boolean> = {};
            let restoredIndex = 0;

            // First, try to restore from sessionStorage (may have latest answers not yet saved to backend)
            // CRITICAL: Only restore if sessionId matches - prevent restoring answers from different sessions
            try {
              const saved = sessionStorage.getItem(storageKey);
              if (saved) {
                const parsed = JSON.parse(saved);

                // CRITICAL: Only restore if sessionId matches
                // If sessionId doesn't match, it means this is a different session
                // Clear storage and don't restore to prevent mixing answers from different sessions
                if (parsed.sessionId && parsed.sessionId !== sessionStatus.sessionId) {
                  sessionStorage.removeItem(storageKey);
                  // Don't restore - start fresh for this session
                } else if (parsed.sessionId === sessionStatus.sessionId) {
                  // SessionId matches - safe to restore

                  // Restore answers from storage first (they may be more recent)
                  // CRITICAL: Check if parsed.answers exists and is not empty
                  if (parsed.answers && typeof parsed.answers === 'object') {
                    const answerKeys = Object.keys(parsed.answers);
                    if (answerKeys.length > 0) {
                      restoredAnswers = { ...parsed.answers };
                      // Also extract flags from answers if flags object is missing or empty
                      for (const [questionId, answer] of Object.entries(restoredAnswers)) {
                        if ((answer as any)?.flagged === true) {
                          restoredFlags[questionId] = true;
                        }
                      }
                    }
                  }
                  // Restore flags (this takes precedence)
                  // CRITICAL: Check if parsed.flags exists and is not empty
                  if (parsed.flags && typeof parsed.flags === 'object') {
                    const flagKeys = Object.keys(parsed.flags);
                    if (flagKeys.length > 0) {
                      restoredFlags = { ...parsed.flags };
                    }
                  }
                  // Restore index
                  if (parsed.index !== undefined) {
                    restoredIndex = parsed.index;
                  }
                }
              }
            } catch (e) {
              // Ignore storage errors, continue with backend data
            }

            // Then, merge/override with backend answers (backend is source of truth for saved answers)
            if (sessionStatus.answers && typeof sessionStatus.answers === 'object') {
              // Backend returns answers as { questionId: { questionId, value } }
              for (const [questionId, answerData] of Object.entries(sessionStatus.answers)) {
                if (answerData && typeof answerData === 'object' && 'value' in answerData) {
                  const answerValue = (answerData as any).value;
                  // Backend answers take precedence (they are saved)
                  // IMPORTANT: Preserve flags from storage when merging with backend answers
                  const existingFlagged =
                    restoredAnswers[questionId]?.flagged || restoredFlags[questionId] || false;
                  restoredAnswers[questionId] = {
                    questionId,
                    value: answerValue,
                    flagged: existingFlagged, // Always preserve flags from storage
                  } as DemoAnswer;
                }
              }
            }

            // CRITICAL: Apply flags to all restored answers AFTER merging with backend
            // This ensures flags from sessionStorage are preserved even if backend doesn't have them
            for (const questionId in restoredFlags) {
              if (restoredFlags[questionId]) {
                if (restoredAnswers[questionId]) {
                  restoredAnswers[questionId].flagged = true;
                } else {
                  // Create answer entry for flagged question even if no answer yet
                  restoredAnswers[questionId] = {
                    questionId,
                    value: null,
                    flagged: true,
                  } as DemoAnswer;
                }
              } else {
                // Explicitly unflag if flag is false
                if (restoredAnswers[questionId]) {
                  restoredAnswers[questionId].flagged = false;
                }
              }
            }

            // Also ensure flags from existing answers are preserved (double-check)
            for (const questionId in restoredAnswers) {
              // If answer was flagged in storage, preserve it
              if (restoredFlags[questionId] !== undefined) {
                restoredAnswers[questionId].flagged = restoredFlags[questionId];
              }
            }

            return {
              answers: restoredAnswers,
              index: restoredIndex || sessionStatus.currentQuestionIndex || 0,
              startedAt: new Date(sessionStatus.startedAt).getTime(),
              expiryTime: expiresAt, // CRITICAL: Use restored expiry time from backend
              sessionId: sessionStatus.sessionId,
              flags: restoredFlags, // CRITICAL: Return flags object
            };
          } else {
            // Session exists but status is not IN_PROGRESS (e.g., COMPLETED)
            // Don't restore - let it fall through to create new session or show error
            // Clear stale storage
            try {
              sessionStorage.removeItem(storageKey);
            } catch (e) {
              // Ignore errors
            }
            return null;
          }
        } else {
          // No session found on backend - this means either:
          // 1. Session was never created
          // 2. Session was deleted/expired
          // 3. User is starting a new exam
          // Clear stale storage - backend is source of truth
          // CRITICAL: Do NOT restore expiryTime from storage when backend has no session
          try {
            sessionStorage.removeItem(storageKey);
          } catch (e) {
            // Ignore errors
          }
          // Return null to trigger new exam start
          return null;
        }
      } catch (error) {
        // Fall through to storage restore
      }
    }

    // Fallback: restore from storage ONLY if backend doesn't have a session
    // CRITICAL: Do NOT restore expiryTime from storage - it may be stale
    // Only restore answers/flags/index, but NOT expiryTime
    // expiryTime must come from backend to ensure accuracy
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);

        // CRITICAL: Do NOT restore expiryTime from storage
        // If backend doesn't have a session, this is a new exam start
        // Return null to trigger new session creation

        // Clear stale storage
        sessionStorage.removeItem(storageKey);
      }
    } catch (error) {
      sessionStorage.removeItem(storageKey);
    }
    return null;
  }, [storageKey, finalExamInfo?.id]);

  // Initialize state from backend or storage
  const [restoredState, setRestoredState] = useState<{
    answers: DemoAnswerMap;
    index: number;
    startedAt: number;
    expiryTime: number;
    sessionId?: string | null;
    flags?: Record<string, boolean>;
    expired?: boolean; // Flag to indicate if session is expired
  } | null>(null);

  // CRITICAL: Clear storage when examId changes (switching to different exam)
  useEffect(() => {
    // Clear storage for any other exam sessions when examId changes
    // This prevents restoring state from a different exam
    if (finalExamInfo?.id) {
      // Clear all exam session storage except current exam
      const currentStorageKey = `exam_session_${finalExamInfo.id}`;
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('exam_session_') && key !== currentStorageKey) {
          sessionStorage.removeItem(key);
        }
      }
    }
  }, [finalExamInfo?.id]);

  // Load session on mount
  useEffect(() => {
    let mounted = true;
    restoreExamState().then(state => {
      if (mounted) {
        setRestoredState(state);
        if (state?.sessionId) {
          setSessionId(state.sessionId);
        }
        setIsLoadingSession(false);

        // Check if session is expired - handle after state is set
        if (state?.expired) {
          // Trigger timeout after a short delay to allow state to settle
          setTimeout(() => {
            if (mounted) {
              // handleTimeout will be defined later, but we'll use doSubmit directly
              setShowTimeoutOverlay(true);
              setTimeout(() => {
                doSubmit();
              }, 2000);
            }
          }, 500);
        }
      }
    });
    return () => {
      mounted = false;
    };
  }, [restoreExamState]);

  const initialStartedAt = restoredState?.startedAt || Date.now();
  // CRITICAL: If restoredState has expiryTime, use it (from backend remainingTime)
  // Only create new expiryTime if there's no restored state (fresh exam start)
  const initialExpiryTime = restoredState?.expiryTime || createExpiryTime(durationMinutes);

  // Session time management
  const startedAtRef = useRef(initialStartedAt);
  const [expiryTime, setExpiryTime] = useState(initialExpiryTime);

  // Update expiryTime when restoredState changes
  useEffect(() => {
    if (restoredState?.expiryTime) {
      setExpiryTime(restoredState.expiryTime);
    }
  }, [restoredState?.expiryTime]);

  // Answers and navigation - restore from storage if available
  // CRITICAL: Initialize with empty object, will be populated by useEffect when restoredState is ready
  const [answers, setAnswers] = useState<DemoAnswerMap>({});
  const [index, setIndex] = useState(0);

  // Extract flags from answers and manage separately for easier persistence
  const extractFlags = useCallback((ans: DemoAnswerMap): Record<string, boolean> => {
    const flags: Record<string, boolean> = {};
    for (const [questionId, answer] of Object.entries(ans)) {
      if (answer?.flagged === true) {
        flags[questionId] = true;
      } else if (answer?.flagged === false) {
        // Explicitly track unflagged questions too (to distinguish from undefined)
        flags[questionId] = false;
      }
    }
    return flags;
  }, []);

  // Update state when restoredState changes
  useEffect(() => {
    if (restoredState) {
      // Merge flags from restoredState into answers
      const mergedAnswers = { ...restoredState.answers };

      // CRITICAL: Apply flags FIRST to ensure they're preserved
      if (restoredState.flags && Object.keys(restoredState.flags).length > 0) {
        for (const [questionId, isFlagged] of Object.entries(restoredState.flags)) {
          if (isFlagged === true) {
            if (mergedAnswers[questionId]) {
              // Update existing answer with flag
              mergedAnswers[questionId].flagged = true;
            } else {
              // Create answer entry for flagged question even if no answer yet
              mergedAnswers[questionId] = {
                questionId,
                value: null,
                flagged: true,
              } as DemoAnswer;
            }
          }
        }
      }

      // Also ensure all answers have their flags set correctly (double-check)
      if (restoredState.flags) {
        for (const questionId in mergedAnswers) {
          // If answer was flagged in storage, preserve it
          if (restoredState.flags[questionId] !== undefined) {
            mergedAnswers[questionId].flagged = restoredState.flags[questionId];
          }
        }
      }

      // CRITICAL: Set answers first, then mark restore complete in a separate effect
      // This ensures answers are populated before we allow saves
      setAnswers(mergedAnswers);
      setIndex(restoredState.index);
      if (restoredState.sessionId) {
        setSessionId(restoredState.sessionId);
      }
    }
  }, [restoredState]);

  // Mark restore as complete AFTER answers have been set
  // Use a separate effect to ensure answers state has been updated
  useEffect(() => {
    // Only mark complete if we have answers OR if restoredState is null (fresh exam)
    if (restoredState === null) {
      // Fresh exam start - allow saves immediately
      setIsRestoreComplete(true);
    } else if (Object.keys(answers).length > 0) {
      // Restored exam with answers - allow saves
      setIsRestoreComplete(true);
    }
  }, [restoredState, answers]);
  const [showTimeoutOverlay, setShowTimeoutOverlay] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [showResetModal, setShowResetModal] = useState(false);

  // Track if we've finished initial restore to prevent saving empty state
  const [isRestoreComplete, setIsRestoreComplete] = useState(false);

  // Persist exam state to storage whenever it changes
  // CRITICAL: Only save if restore is complete AND we have actual data to prevent overwriting with empty state
  useEffect(() => {
    // Don't save if restore is not complete yet (prevents saving empty state on mount)
    if (!isRestoreComplete) {
      return;
    }

    // CRITICAL: Don't save if answers is empty (prevents overwriting with empty state)
    if (Object.keys(answers).length === 0) {
      return;
    }

    try {
      const flags = extractFlags(answers);
      const stateToSave = {
        answers,
        flags, // Store flags separately for easier restoration
        index,
        startedAt: startedAtRef.current,
        expiryTime,
        sessionId, // CRITICAL: Store sessionId to verify on restore
        examId: finalExamInfo?.id || 'demo',
        timestamp: Date.now(),
      };
      const savedString = JSON.stringify(stateToSave);
      sessionStorage.setItem(storageKey, savedString);
    } catch (error) {
      // Ignore save errors
    }
  }, [
    answers,
    index,
    expiryTime,
    storageKey,
    finalExamInfo?.id,
    sessionId,
    extractFlags,
    isRestoreComplete,
  ]);

  // Sync remaining time from backend every 30 seconds (if we have a session)
  useEffect(() => {
    if (!finalExamInfo?.id || !sessionId || isLoadingSession) return;

    const syncInterval = setInterval(async () => {
      if (!finalExamInfo?.id) return;
      try {
        const sessionStatus = await apiService.getSessionStatus(finalExamInfo.id);
        if (
          sessionStatus &&
          sessionStatus.status === 'IN_PROGRESS' &&
          sessionStatus.remainingTime > 0
        ) {
          // Update expiryTime based on backend remaining time
          const now = Date.now();
          const newExpiryTime = now + sessionStatus.remainingTime;
          setExpiryTime(newExpiryTime);
        } else if (sessionStatus?.expired) {
          // Session expired on backend, trigger timeout via countdown hook
          // The countdown hook will call handleTimeout when expiryTime is reached
          setExpiryTime(Date.now()); // Force immediate expiry
        }
      } catch (error) {
        // Ignore sync errors
      }
    }, 30000); // Sync every 30 seconds

    return () => clearInterval(syncInterval);
  }, [finalExamInfo?.id, sessionId, isLoadingSession]);

  // Cleanup auto-save timers on unmount
  useEffect(() => {
    return () => {
      autoSaveTimerRef.current.forEach(timer => clearTimeout(timer));
      autoSaveTimerRef.current.clear();
    };
  }, []);

  // Clear storage on submit or exit
  const clearExamState = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      // Ignore clear errors
    }
  }, [storageKey]);

  // Show tour intro for first-time users
  useEffect(() => {
    if (!hasSeenTour) {
      setShowTourIntro(true);
    }
  }, [hasSeenTour]);

  /** Internal submit function that bypasses modal (for timeout/auto-submit) */
  const doSubmit = useCallback(() => {
    try {
      const { items, total, earned, breakdown } = computeScores(questions as any, answers);
      const finishedAt = Date.now();
      const durationSeconds = Math.max(1, Math.round((finishedAt - startedAtRef.current) / 1000));
      const remainingMs = expiryTime - finishedAt;
      const summary: DemoResultSummary = {
        totalPoints: total,
        earnedPoints: earned,
        items,
        startedAt: startedAtRef.current,
        finishedAt,
        durationSeconds,
        remainingSeconds: Math.max(0, Math.floor(remainingMs / 1000)),
        breakdown,
      };

      // Clear storage on submit
      clearExamState();

      // Call parent onSubmit callback
      // Note: onSubmit may be async, but we don't await it here
      // Parent component will handle async operations and navigation
      onSubmit(summary, answers);
    } catch (error) {
      console.error('Error in doSubmit:', error);
      // Still clear storage even on error
      clearExamState();
      // Re-throw to let parent handle
      throw error;
    }
  }, [questions, answers, onSubmit, expiryTime, clearExamState]);

  /** Handle timeout - show overlay then auto-submit */
  const handleTimeout = useCallback(() => {
    setShowTimeoutOverlay(true);
    setTimeout(() => {
      doSubmit();
    }, 2000);
  }, [doSubmit]);

  /** Submit summary to parent (called after user confirms in modal). */
  const handleSubmit = useCallback(() => {
    // Note: Submit confirmation is handled by ScientificExamLayout modal
    // This function is called after user confirms in the modal
    try {
      // Call doSubmit synchronously - it will call onSubmit which is async
      // but we don't need to await it here as parent handles navigation
      doSubmit();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      // Show error to user
      toast.error('Có lỗi xảy ra khi nộp bài. Vui lòng thử lại.');
      // Re-throw to ensure error is visible
      throw error;
    }
  }, [doSubmit]);

  const countdown = useCountdown({
    expiryTime,
    onTimeout: handleTimeout,
    paused: isTourRunning, // Pause timer during tour
  });

  // Security monitoring - disabled during tour
  const security = useExamSecurity(handleSubmit, finalExamConfig, isTourRunning);

  // Auto-reset security on new exam start
  useEffect(() => {
    security.resetExamSecurity('demo');
  }, []);

  // Reset security on exam start
  useEffect(() => {
    if (security.violationCount > 0) {
      setShowResetModal(true);
    }
  }, []);

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Prevent page leave with confirmation dialog (browser close/tab switch)
  // Note: State is automatically saved to sessionStorage, so it will persist on refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Save state one more time before unload (in case of rapid changes)
      try {
        const flags = extractFlags(answers);
        const stateToSave = {
          answers,
          flags, // Store flags separately for easier restoration
          index,
          startedAt: startedAtRef.current,
          expiryTime,
          examId: finalExamInfo?.id || 'demo',
          sessionId, // Include sessionId
          timestamp: Date.now(),
        };
        sessionStorage.setItem(storageKey, JSON.stringify(stateToSave));
      } catch (error) {
        // Ignore save errors
      }

      // Show browser confirmation dialog
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome
      return ''; // Required for some browsers
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [answers, index, expiryTime, storageKey, finalExamInfo?.id, sessionId, extractFlags]);

  // Prevent browser back button navigation
  useEffect(() => {
    // Push a state to history to detect back button
    const handlePopState = (e: PopStateEvent) => {
      // Prevent navigation and show confirmation
      window.history.pushState(null, '', window.location.href);
      setPendingNavigation(() => () => onExit());
      setShowLeaveConfirm(true);
    };

    // Push state when component mounts
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onExit]);

  const handleResetSecurity = () => {
    const session = security.resetExamSecurity('demo');
    setShowResetModal(false);
    toast.success('Exam started with clean security state', {
      duration: 3000,
      className: 'border-green-500 bg-green-50 text-green-900',
    });
  };

  const curr = questions[index];
  const isExpired = countdown.isExpired;

  /** Update answer for any question by ID. */
  const updateQuestionAnswer = (
    questionId: string,
    value:
      | string
      | number
      | boolean
      | Record<string, string | null>
      | Record<string, boolean>
      | string[]
  ) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        questionId,
        value,
        flagged: prev[questionId]?.flagged ?? false,
      } as DemoAnswer,
    }));
  };

  // Auto-save debounce timer ref
  const autoSaveTimerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Convert frontend answer format to backend format
  const convertAnswerToBackendFormat = useCallback(
    (questionId: string, questionType: string, value: any): any => {
      const answerData: any = {
        sessionId: sessionId || '',
        questionId,
      };

      switch (questionType) {
        case 'mc':
        case 'mcq':
          if (Array.isArray(value)) {
            answerData.selectedOptions = value;
          } else if (typeof value === 'string' || typeof value === 'number') {
            answerData.selectedOption = String(value);
          }
          break;
        case 'tf':
        case 'tf_matrix':
        case 'trueFalse': // Add support for 'trueFalse' type
          if (typeof value === 'object' && value !== null) {
            answerData.trueFalseAnswers = value;
          }
          break;
        case 'matching':
          if (typeof value === 'object' && value !== null) {
            answerData.matchingAnswers = value;
          }
          break;
        case 'cloze':
        case 'fill_blank':
          if (typeof value === 'object' && value !== null) {
            answerData.fillAnswers = value;
          }
          break;
        case 'essay':
          if (typeof value === 'string') {
            answerData.essayText = value;
          }
          break;
        case 'short':
        case 'short_text':
        case 'short_numeric':
          if (typeof value === 'string') {
            answerData.selectedOption = value; // Store as selectedOption for short answers
          } else if (typeof value === 'number') {
            answerData.selectedOption = String(value);
          }
          break;
      }

      return answerData;
    },
    [sessionId]
  );

  // Auto-save answer to backend
  const autoSaveAnswer = useCallback(
    async (questionId: string, questionType: string, value: any) => {
      if (!sessionId || !finalExamInfo?.id) {
        return; // No session or exam ID, skip auto-save
      }

      // Clear existing timer for this question
      const existingTimer = autoSaveTimerRef.current.get(questionId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer (debounce 500ms)
      const timer = setTimeout(async () => {
        try {
          const answerData = convertAnswerToBackendFormat(questionId, questionType, value);
          if (answerData.sessionId && finalExamInfo?.id) {
            await apiService.saveAnswer(finalExamInfo.id, answerData);
          }
        } catch (error) {
          // Don't show error to user, just ignore it
        } finally {
          autoSaveTimerRef.current.delete(questionId);
        }
      }, 500);

      autoSaveTimerRef.current.set(questionId, timer);
    },
    [sessionId, finalExamInfo?.id, convertAnswerToBackendFormat]
  );

  /** Update answer for current question. */
  const setAnswer = (
    val:
      | string
      | number
      | boolean
      | Record<string, string | null>
      | Record<string, boolean>
      | string[]
  ) => {
    // Special handling for cloze/fillBlank questions in clusters
    // When val is an object with blankId keys (e.g., { "blank0": "answer1", "blank1": "answer2" }),
    // we need to distribute answers to the correct questions based on position in passage
    if (
      curr?.type === 'fillBlank' &&
      typeof val === 'object' &&
      val !== null &&
      !Array.isArray(val) &&
      curr.clusterContext &&
      curr.clusterQuestionCount &&
      curr.clusterQuestionCount > 1
    ) {
      // This is a multi-blank cloze question
      // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
      // For single sub-cluster, get all questions in cluster
      const clusterQuestions = questions
        .filter((q: any) => {
          // For multiple sub-clusters, only get questions with the same questionClusterId
          if ((curr as any).questionClusterId) {
            return (
              q.clusterId === curr.clusterId &&
              q.type === 'fillBlank' &&
              (q as any).questionClusterId === (curr as any).questionClusterId
            );
          }
          // For single sub-cluster, get all questions in cluster
          return q.clusterId === curr.clusterId && q.type === 'fillBlank';
        })
        .sort((a: any, b: any) => {
          // Sort by clusterQuestionIndex if available, otherwise by order field
          if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
            return a.clusterQuestionIndex - b.clusterQuestionIndex;
          }
          // Fallback to order field (1-based question number in exam)
          const aOrder = (a as any).order || 0;
          const bOrder = (b as any).order || 0;
          return aOrder - bOrder;
        });

      // CRITICAL: val is now keyed by questionId (e.g., { "questionId1": "answer1", "questionId2": "answer2" })
      // No more blankId mapping needed - questionId is already the correct key
      const questionAnswers = val as Record<string, string | null>;

      // For each questionId in questionAnswers, save answer directly
      // No need to map blankId → position → questionId anymore
      Object.entries(questionAnswers).forEach(([targetQuestionId, answerValue]) => {
        // Verify this questionId belongs to the cluster
        const targetQuestion = clusterQuestions.find(q => q.id === targetQuestionId);

        if (targetQuestion) {
          // Save answer for the target question
          // CRITICAL: Save as string value, NOT as { blankId: answerValue }
          // Backend will handle conversion from questionId to blankId when needed
          setAnswers(prev => ({
            ...prev,
            [targetQuestionId]: {
              questionId: targetQuestionId,
              value: answerValue, // Save as string value, not { blankId: answerValue }
              flagged: prev[targetQuestionId]?.flagged ?? false,
            } as DemoAnswer,
          }));

          // Auto-save to backend for the target question
          // CRITICAL: Use questionId as key in fillAnswers (backend will map to blankId)
          if (targetQuestionId && targetQuestion.type) {
            const fillAnswers: Record<string, string> = {};
            // Use questionId as key - backend will map to blankId when grading
            fillAnswers[targetQuestionId] = String(answerValue || '');
            autoSaveAnswer(targetQuestionId, targetQuestion.type, fillAnswers);
          }
        }
      });
    } else {
      // Normal handling for non-cloze questions or single-blank questions
      setAnswers(prev => ({
        ...prev,
        [curr.id]: {
          questionId: curr.id,
          value: val,
          flagged: prev[curr.id]?.flagged ?? false,
        } as DemoAnswer,
      }));

      // Auto-save to backend
      if (curr?.id && curr?.type) {
        autoSaveAnswer(curr.id, curr.type, val);
      }
    }
  };

  /** Toggle flag for review. */
  const toggleFlag = () => {
    setAnswers(prev => {
      const newFlagged = !prev[curr.id]?.flagged;
      return {
        ...prev,
        [curr.id]: {
          questionId: curr.id,
          value: prev[curr.id]?.value ?? null,
          flagged: newFlagged,
        } as DemoAnswer,
      };
    });
  };

  /** Confirm exit if user tries to leave */
  const handleExit = () => {
    // Show confirmation modal instead of window.confirm
    setShowLeaveConfirm(true);
    setPendingNavigation(() => () => {
      // DON'T clear storage on exit - answers are auto-saved to backend
      // User can resume later by restoring from backend session
      // Only clear on submit or timeout
      onExit();
    });
  };

  /** Navigation handlers */
  const handlePrevious = () => setIndex((i: number) => Math.max(0, i - 1));
  const handleNext = () => setIndex((i: number) => Math.min(questions.length - 1, i + 1));
  const handleJumpToQuestion = (newIndex: number) => setIndex(newIndex);

  /** Handle keyboard option selection */
  const handleSelectOption = (optionIndex: number) => {
    if (curr.type === 'mc' && curr.options && optionIndex < curr.options.length) {
      const option = curr.options[optionIndex];
      setAnswer(typeof option === 'string' ? option : option.id);
    } else if (curr.type === 'tf' && optionIndex < 2) {
      setAnswer(optionIndex === 0); // 1 = true, 2 = false
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (isExpired) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          e.preventDefault();
          handleSelectOption(parseInt(e.key) - 1);
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleFlag();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case 'Enter':
          if (e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [curr, isExpired]);

  // Notify parent of index changes (for lazy loading)
  useEffect(() => {
    onPageChange?.(index);
  }, [index, onPageChange]);

  return (
    <>
      {/* Tour Intro Dialog */}
      <ExamTourIntro
        isOpen={showTourIntro}
        onStart={() => {
          setShowTourIntro(false);
          startTour();
        }}
        onSkip={() => {
          setShowTourIntro(false);
          endTour();
        }}
      />

      {/* Guided Tour */}
      <ExamTour onComplete={() => {}} />

      {/* Timeout Overlay */}
      {showTimeoutOverlay && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
          <div className='rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 text-center shadow-xl border border-border group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
            <div className='w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-orange-500/25'>
              <AlertCircle className='h-8 w-8 text-white' />
            </div>
            <h2 className='mt-4 text-xl font-semibold text-foreground'>Hết thời gian</h2>
            <p className='mt-2 text-muted-foreground'>Bài sẽ được nộp tự động...</p>
          </div>
        </div>
      )}

      <ScientificExamLayout
        questions={questions as any}
        answers={answers}
        currentIndex={index}
        timeRemaining={countdown.remaining}
        zoom={zoom}
        securityLevel={security.securityLevel}
        violationCount={security.violationCount}
        maxViolations={security.maxViolations}
        isTabActive={security.isTabActive}
        onJumpToQuestion={handleJumpToQuestion}
        onZoomChange={setZoom}
        onSubmit={handleSubmit}
        onExit={onExit}
        examInfo={finalExamInfo}
      >
        <div className='relative'>
          {curr && (curr as any).isLoaded !== false ? (
            <GenericQuestionContent
              question={curr as any}
              answer={answers[curr.id]}
              questionIndex={index}
              totalQuestions={questions.length}
              isExpired={isExpired}
              onChange={setAnswer}
              onToggleFlag={toggleFlag}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onSubmit={handleSubmit}
              allAnswers={answers}
              allQuestions={questions as any}
              shuffledOrders={shuffledOrders}
              onShuffledOrderChange={(questionId, order) => {
                setShuffledOrders(prev => ({
                  ...prev,
                  [questionId]: order,
                }));
              }}
              onNavigateToQuestion={handleJumpToQuestion}
              examSubject={finalExamInfo?.subject}
            />
          ) : (
            <div className='flex flex-col items-center justify-center min-h-[400px] text-muted-foreground animate-pulse'>
              <div className='w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4'></div>
              <p>Đang tải câu hỏi...</p>
            </div>
          )}
        </div>
      </ScientificExamLayout>

      {/* Tab Return Warning Modal */}
      <TabReturnModal
        isTabActive={security.isTabActive}
        violationCount={security.violationCount}
        onAcknowledge={() => {}}
      />

      {/* Security Reset Modal */}
      <SecurityResetModal
        isOpen={showResetModal}
        onConfirm={handleResetSecurity}
        onCancel={() => setShowResetModal(false)}
        examName='Demo Exam'
      />

      {/* Leave Exam Confirmation Modal */}
      {showLeaveConfirm && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 max-w-md w-full mx-4 shadow-xl border border-border'>
            <div className='flex items-center gap-3 mb-4'>
              <AlertTriangle className='w-5 h-5 lg:w-6 lg:h-6 text-amber-500 flex-shrink-0' />
              <h3 className='text-base lg:text-lg font-semibold text-foreground'>
                {t('landing.exam.layout.confirmLeave.title', {
                  defaultValue: 'Xác nhận rời khỏi bài thi',
                })}
              </h3>
            </div>

            <p className='text-sm lg:text-base text-muted-foreground mb-6'>
              {t('landing.exam.layout.confirmLeave.message', {
                defaultValue:
                  'Tiến trình làm bài đã được lưu tự động. Bạn có thể quay lại tiếp tục làm bài sau. Bạn có chắc muốn rời khỏi bài thi?',
              })}
            </p>

            <div className='flex gap-3 justify-end'>
              <Button
                variant='outline'
                onClick={() => {
                  setShowLeaveConfirm(false);
                  setPendingNavigation(null);
                }}
                className='text-sm lg:text-base border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
              >
                {t('landing.exam.layout.confirmLeave.cancel', { defaultValue: 'Ở lại' })}
              </Button>
              <Button
                onClick={() => {
                  setShowLeaveConfirm(false);
                  if (pendingNavigation) {
                    pendingNavigation();
                  }
                  setPendingNavigation(null);
                }}
                className='bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white text-sm lg:text-base shadow-lg shadow-primary/25 transition-all duration-300'
              >
                {t('landing.exam.layout.confirmLeave.confirm', { defaultValue: 'Rời khỏi' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
